/**
 * The deployment environment, shared by every TS surface that has to branch on
 * it. Mirrors the `Environment` enum in
 * `backends/libs/vellum-utils/vellum_utils/environments.py` — keep the two in
 * lockstep.
 *
 * A const object rather than a TS `enum`: this module is consumed as raw TS by
 * both Next and the Vite micro apps under `isolatedModules`, and `enum` is
 * erasable-syntax-hostile (Node's type stripping rejects it outright).
 *
 * This is a leaf module with no imports, so it is safe to pull in through the
 * `@vellum/ui/environment` subpath from packages that `@vellum/ui` itself
 * depends on — same arrangement as `@vellum/ui/fetch`.
 */

export const Environment = {
  test: "test",
  local: "local",
  dev: "dev",
  stage: "stage",
  prod: "prod",
} as const;

export type Environment = (typeof Environment)[keyof typeof Environment];

export const ENVIRONMENTS: readonly Environment[] = Object.values(Environment);

/**
 * The subset that is actually reachable as a public web origin. The browser
 * can only tell these three apart by hostname, and they are the only ones with
 * real hosts, so origin maps are keyed by this rather than the full set.
 */
export type HostedEnvironment = Extract<Environment, "local" | "dev" | "prod">;

export function isEnvironment(value: unknown): value is Environment {
  return (
    typeof value === "string" &&
    (ENVIRONMENTS as readonly string[]).includes(value)
  );
}

const VALID = `Valid values are ${ENVIRONMENTS.join(", ")} (staging is accepted as an alias for stage).`;

/**
 * Normalize a raw `ENVIRONMENT` value (e.g. `"DEV"`, `"staging"`) to the
 * canonical environment.
 *
 * Throws on both an unset and an unrecognized value — never guesses. A missing
 * or typo'd `ENVIRONMENT` should take the process down at boot rather than
 * silently behaving like some other environment, which is exactly the failure
 * mode PEAK-479 was. Matches `get_environment` in
 * `vellum_utils.environments`, which raises on the same two conditions, and
 * the `ARG ENVIRONMENT` guard both frontend Dockerfiles already carry.
 *
 * Named for what it does — turning a raw string into an `Environment`. The
 * hostname-derived, zero-argument `resolveEnvironment()` in
 * `@vellum/micro-app-auth` is a different question with a different answer.
 */
export function normalizeEnvironment(
  rawEnvironment: string | undefined | null,
): Environment {
  const value = (rawEnvironment ?? "").trim().toLowerCase();
  if (value === "") {
    throw new Error(`ENVIRONMENT is not set. ${VALID}`);
  }
  if (value === "staging") {
    return Environment.stage;
  }
  if (isEnvironment(value)) {
    return value;
  }
  throw new Error(`Unknown ENVIRONMENT "${rawEnvironment}". ${VALID}`);
}
